import { Injectable, EventEmitter } from "@angular/core";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { WebshopLanguagesObjectObserverData } from "./objectObserver.service";
import { ContentType, TranslationContentType, IsTranslationContentTypeInCentral } from "../shared/contentTypes";
import { WebshopLanguagesDataModel } from "../shared/models/webshopLanguages.model";
import { ICoreLanguageManagerService } from "./ILanguageManager";

/** 
 * Sprachenmanager, der es ermöglicht die unterschiedlichen Übersetzungen bereitzustellen.
 * Es muss beim Sprachwechsel die benötigte Sprache geladen werden und erst danach wird die Sprache abgerufen.
 * Dafür ist der Abruf danach synchron!
 */
@Injectable({
  providedIn: "root" // sollte nur mit der Anwendung zerstört werden
})
export class CoreLanguageManagerService implements ICoreLanguageManagerService {
  constructor(
    private _coreLogService: CoreLogService,
    private _objObsInjectHelper: CircularInjectionHelper) {
    // Es ist ein root, sollte nur einmal generiert werden und daraufhin immer genutzt werden
    // darum kann man es global zugreifbar machen
    // TODO BAU double locked SINGLETON !
    (window as any).ya_CoreLanguageManagerService = this;
  }

  public ngOnDestroy(): void {
    // Warum wurde der Destruktor aufgerufen?
    // der Service sollte solange existieren, solange die App läuft
    (window as any).ya_CoreLanguageManagerService = null;
  }

  /** Ist der Interne status, ob die Sprachen genutzt werden können. 
   * Wenn true, dann darf erst eine Sprache abgefragt werden. */
  private _ready: boolean = false;
  /** um die Location nicht immer übergeben zu müssen, kann sie auf ein default wert gesetzt werden */
  private _location: number | -1 = 0;
  /** um die Sprache nicht immer übergeben zu müssen, kann sie auf ein default wert gesetzt werden */
  private _language_code: string = null;
  /** aktuel geladener Standort */
  public get location(): number | -1 {
    return this._location;
  }
  /** aktuel geladene Sprache */
  public get language_code(): string {
    return this._language_code;
  }
  /** Zentrale Sprache */
  private _centralLangauge: WebshopLanguagesDataModel = null;
  /** Standort Sprache */
  private _locationLangauge: WebshopLanguagesDataModel = null;

  /** 
   * Lädt die Sprachen zu dem gewünschten Standort und LanguageCode.
   * Sie ist Asynchrone und muss Abgewartet werden, bevor die Sprachen abgerufen werden können.
   */
  public async LoadLanguagesAsync(location: number | -1, language_code: string): Promise<void> {
    const me = this;
    if (me._location === location && me._language_code === language_code) {
      return; // bereits geladen
    }

    me._ready = false;

    // zentrale muss nur einmal geladen werden, oder wenn sich die Sprache ändert
    if (me._centralLangauge == null || me._centralLangauge.code != language_code) {
      try {
        if (language_code === "de") {
          me._centralLangauge = null; // keine übersetzung für "de" notwendig
        } else {
          // ! warum ist er hier beim zweiten laden für immer weg?
          me._centralLangauge = await CoreLanguageManagerService._loadLanguageAsync(me._objObsInjectHelper, -1 /* -1=Zentrale */, language_code);
        }
      } catch (error) {
        me._coreLogService.error("CoreLanguageManagerService.LoadLanguagesAsync", "load central language error", error, language_code);
      }
    }

    if (me._locationLangauge != null && me._locationLangauge != me._centralLangauge) {
      // unload location /release memory
      me._locationLangauge = null;
    }

    if (location === -1) {
      me._locationLangauge = me._centralLangauge;
    } else {
      try {
        if (language_code === "de") {
          me._locationLangauge = null; // keine übersetzung für "de" notwendig
        } else {
          me._locationLangauge = await CoreLanguageManagerService._loadLanguageAsync(me._objObsInjectHelper, location, language_code);
        }
      } catch (error) {
        me._coreLogService.error("CoreLanguageManagerService.LoadLanguagesAsync", "load location language error", error, location, language_code);
      }
    }

    me._location = location;
    me._language_code = language_code
    me._ready = true;
  }

  /** versucht die sprache entsprechend zu laden und zurückzugeben, und leert observer spreicher */
  private static async _loadLanguageAsync(helper: CircularInjectionHelper, location: number | -1, language_code: string): Promise<WebshopLanguagesDataModel> {
    let languages: WebshopLanguagesDataModel = null;
    // load location
    const request_data = new WebshopLanguagesObjectObserverData(location, language_code);
    const WebshopLanguagesObjObserver = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopLanguagesDataModel>(ContentType.sub_WebshopLanguages, helper, request_data);
    const data = await WebshopLanguagesObjObserver.GetAllObjects();
    if (data && data.length > 0 && !data[0].deleted) {
      languages = data[0];
    }
    // speicher freigeben
    ObjectObserverFactory.ReleaseObjectObserver(ContentType.sub_Product, request_data);
    return languages;
  }

  /** Übersetzt die angefragten Daten */
  public translate(contentType: TranslationContentType, contentID: number, de_defaultValue: string): string {
    const me = this;
    if (!me._ready) {
      me._coreLogService.debug("CoreLanguageManagerService.translate", "not ready, need wait to complete load");
      return de_defaultValue;
    }

    let translation: string = null;
    if (IsTranslationContentTypeInCentral(contentType)) {
      if (me._centralLangauge != null) {
        translation = me._centralLangauge.GetTranslation(contentType, contentID, translation);
      }
    } else {
      if (me._locationLangauge != null) {
        translation = me._locationLangauge.GetTranslation(contentType, contentID, translation);
      }
      // todo, sollte ein fallback eintretten, um die zentrale zu nutzen?
    }

    if (translation == null) {
      me._coreLogService.debug("CoreLanguageManagerService.translate", "translation not found", contentType, contentID, de_defaultValue, me.location, me.language_code);
      return de_defaultValue;
    }
    return translation;
  }
}
